import React, { FC, ChangeEvent, FocusEvent, useRef } from "react";
import {
  Controller,
  Control,
  FieldError,
  UseControllerOptions
} from "react-hook-form";
import RHTextField, { RHTextFieldProps } from "./index";
import memoize from "utils/memoize";

export type RHFormTextFieldProps = Omit<RHTextFieldProps, "error"> & {
  control: Control;
  rules?: UseControllerOptions["rules"];
  onInputBlur?: (
    e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    name: string
  ) => void;
  onInputChange?: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    name: string
  ) => void;
  error?: FieldError;
  emailValidation?: Boolean;
  errorMessages?: Object;
  isCompanyFieldRequired?: any;
};

const RHFormTextField: FC<RHFormTextFieldProps> = ({
  control,
  label,
  name,
  defaultValue,
  children,
  onInputBlur,
  onInputChange,
  rules,
  select,
  error,
  emailValidation = false,
  onChange: onChange,
  onBlur,
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      rules={rules}
      render={controllerRenderProps => {
        return (
          <RHTextField
            select={select}
            value={emailValidation ? defaultValue : controllerRenderProps.value}
            label={label}
            inputRef={el => {
              if (el) {
                inputRef.current = el;
                if (controllerRenderProps.ref) {
                  (
                    controllerRenderProps.ref as React.MutableRefObject<HTMLInputElement | null>
                  ).current = el;
                }
              }
            }}
            onFocus={() => {
              if (inputRef.current) {
                inputRef.current.disabled = false;
                if (inputRef.current.scrollIntoView) {
                  inputRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "center"
                  });
                }
                inputRef.current.focus({ preventScroll: true });
              }
            }}
            onBlur={e => {
              controllerRenderProps.onBlur();
              if (onBlur) onBlur(e);
              if (onInputBlur) onInputBlur(e, name);
            }}
            onChange={e => {
              if (!emailValidation) controllerRenderProps.onChange(e);
              if (onChange) onChange(e);
              if (onInputChange) onInputChange(e, name);
            }}
            error={Boolean(error)}
            helperText={error?.message}
            dropdowntype={true}
            {...(rest as RHTextFieldProps)}
          >
            {children}
          </RHTextField>
        );
      }}
    />
  );
};

RHFormTextField.defaultProps = {
  defaultValue: ""
};

export default memoize(RHFormTextField);
